import React, { useRef, useState, useEffect } from 'react';
import styles from './FeaturedCharacters.module.css';
import { Character } from './ExploreDesktop';
import { MAX_FEATURED_ITEMS } from './Explore'; // Import the constant

interface FeaturedCharactersProps {
  characters: Character[];
  onCharacterClick: (character: Character) => void;
  isLoading?: boolean;
}

const FeaturedCharacters: React.FC<FeaturedCharactersProps> = ({ 
  characters, 
  onCharacterClick,
  isLoading = false
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showPlaceholders, setShowPlaceholders] = useState(true);
  const [loadedCharacters, setLoadedCharacters] = useState<{[id: string]: boolean}>({});
  const [transitionStarted, setTransitionStarted] = useState(false);
  
  // Handle loading state changes
  useEffect(() => {
    // Always show placeholders initially or when loading state changes
    setShowPlaceholders(true);
    setLoadedCharacters({});
    setTransitionStarted(false);
    
    // If there are no characters or we're in loading state, keep showing placeholders
    if (characters.length === 0 || isLoading) {
      return;
    }
    
    // Start transition after a minimum delay even if not all images are loaded
    const minLoadTimer = setTimeout(() => {
      if (Object.keys(loadedCharacters).length > 0) {
        startTransition();
      }
    }, 800);
    
    // Force transition after a maximum wait time
    const maxWaitTimer = setTimeout(() => {
      startTransition();
    }, 2000);
    
    return () => {
      clearTimeout(minLoadTimer);
      clearTimeout(maxWaitTimer);
    };
  }, [characters, isLoading]);
  
  // Start transition from placeholders to content
  const startTransition = () => {
    if (!transitionStarted) {
      setTransitionStarted(true);
      setTimeout(() => {
        setShowPlaceholders(false);
      }, 300);
    }
  };
  
  // Load each character image individually
  useEffect(() => {
    if (characters.length === 0 || isLoading) return;
    
    // Track which characters have loaded
    const newLoadedState = { ...loadedCharacters };
    let loadedCount = 0;
    
    // Preload each character image
    characters.forEach(character => {
      // Skip already loaded images
      if (newLoadedState[character.id]) return;
      
      const img = new Image();
      
      img.onload = () => {
        // Mark this character as loaded
        newLoadedState[character.id] = true;
        setLoadedCharacters({ ...newLoadedState });
        
        loadedCount++;
        // If we have at least 2 characters or 50% loaded, start transition
        if (loadedCount >= 2 || loadedCount >= characters.length / 2) {
          startTransition();
        }
      };
      
      img.onerror = () => {
        // Mark as loaded even on error to avoid blocking
        newLoadedState[character.id] = true;
        setLoadedCharacters({ ...newLoadedState });
      };
      
      // Start loading the image
      img.src = character.icon;
    });
  }, [characters, isLoading, loadedCharacters]);
  
  const handleScroll = (direction: 'prev' | 'next') => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    // Adjust scroll amount to match the width of one card (150px) plus gap (10px)
    const scrollAmount = direction === 'next' ? 160 : -160; // 150px card + 10px gap
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  // Create placeholder items - using the same constant as the parent component
  const placeholders = Array(MAX_FEATURED_ITEMS).fill(null);
  
  // Check if a character is loaded
  const isCharacterLoaded = (id: string) => {
    return loadedCharacters[id] === true;
  };
  
  return (
    <div className={styles.featuredCharactersContainer}>
      <h2 className={styles.sectionTitle}>Featured Characters</h2>
      
      <div className={styles.carouselContainer}>
        <button 
          className={`${styles.navButton} ${styles.prevButton}`}
          onClick={() => handleScroll('prev')}
          aria-label="Previous characters"
          disabled={showPlaceholders}
        >
          &lt;
        </button>
        
        {/* Always render both content and placeholders, control visibility with CSS */}
        <div className={styles.contentWrapper}>
          {/* Placeholders */}
          {showPlaceholders && (
            <div 
              ref={scrollContainerRef}
              className={`${styles.charactersGrid} ${styles.placeholderGrid}`}
            >
              {placeholders.map((_, index) => (
                <div 
                  key={`placeholder-${index}`}
                  className={`${styles.characterCard} ${styles.placeholderCard}`}
                >
                  <div className={`${styles.characterImage} ${styles.placeholderImage}`}>
                    <div className={styles.shimmerEffect}></div>
                  </div>
                  
                  <div className={styles.characterInfo}>
                    <div className={`${styles.characterName} ${styles.placeholderName}`}>
                      <div className={styles.shimmerEffect}></div>
                    </div>
                    <div className={`${styles.characterTagline} ${styles.placeholderTagline}`}>
                      <div className={styles.shimmerEffect}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Actual content - always render but control visibility */}
          <div 
            ref={!showPlaceholders ? scrollContainerRef : undefined}
            className={`${styles.charactersGrid} ${transitionStarted ? styles.fadeIn : styles.hidden}`}
            style={{ display: showPlaceholders ? 'none' : 'grid' }} // Changed from 'flex' to 'grid'
          >
            {characters.map((character) => (
              <div 
                key={character.id}
                className={`${styles.characterCard} ${isCharacterLoaded(character.id) ? styles.characterLoaded : ''}`}
                onClick={() => onCharacterClick(character)}
              >
                <div className={styles.characterImage}>
                  <img 
                    src={character.icon} 
                    alt={character.display_name} 
                    className={isCharacterLoaded(character.id) ? styles.imageLoaded : styles.imageLoading}
                  />
                </div>
                
                <div className={styles.characterInfo}>
                  <h3 className={styles.characterName}>{character.display_name}</h3>
                  <p className={styles.characterTagline}>
                    {character.tagline || "The character is a living weapon, it isn't supposed to have feelings..."}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <button 
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={() => handleScroll('next')}
          aria-label="Next characters"
          disabled={showPlaceholders}
        >
          &gt;
        </button>
      </div>
    </div>
  );
};

export default FeaturedCharacters; 